import { useEffect, useState } from "react";

/**
 * Each workspace's Critical, High and Medium issue counts, each issue at its highest level over the
 * measures the workspace has (feature_watchdog/all-workspaces-marker/refinement.md). The mock reads them from
 * public/watchdog/criticality.json, written by
 * workbench/backlog/feature_watchdog/all-workspaces-marker/notes/gen-criticality.js and gitignored there.
 */
export const LEVELS = ["critical", "high", "medium"];

const NONE = { critical: 0, high: 0, medium: 0 };

/** The counts of `workspaceNumber`, all zero for a workspace the file leaves out. */
const countsFor = (counts, workspaceNumber) => counts[workspaceNumber] || NONE;

/**
 * `workspaces` with the most Critical issues first, ties broken by High, then by Medium; a workspace
 * with none of them keeps its place relative to the others with none.
 */
export const inCriticalityOrder = (workspaces, counts) => {
    const compare = (a, b) => {
        const [x, y] = [countsFor(counts, a.seqNumber), countsFor(counts, b.seqNumber)];
        return LEVELS.reduce((order, level) => order || y[level] - x[level], 0);
    };
    return [...workspaces].sort(compare);
};

let pending = null;

// Absolute, because package.json sets "homepage": "." and a relative URL would resolve against the route.
const fetchCounts = () => {
    if (!pending) {
        pending = fetch("/watchdog/criticality.json")
            // A build without the file answers every path with index.html and a 200.
            .then((response) => (response.ok && (response.headers.get("content-type") || "").includes("json") ? response.json() : {}))
            .catch(() => ({}));
    }
    return pending;
};

/** The counts by workspace number; null until fetched, empty when there is no file. */
export const useCriticalityCounts = () => {
    const [counts, setCounts] = useState(null);
    useEffect(() => {
        let live = true;
        fetchCounts().then((fetched) => live && setCounts(fetched));
        return () => { live = false; };
    }, []);
    return counts;
};
